import MeasurementUnit from '../../controllers/MeasurementUnit'
import Papa from 'papaparse'

export type CSVPopulate<T> = (value: string, entry: T, system: MeasurementUnit) => void;

export type ColumnMatcher<T> = {
    match: (header: string) => boolean;
    populate: CSVPopulate<T>;
};

export function matchArray(...headers: string[]): (header: string) => boolean {
    const normalized = headers.map(h => h.toLowerCase().trim());
    return (header: string) => normalized.includes(header.toLowerCase().trim());
}

function convertKeys<T>(rec: Record<string, T>, keyConverter: (key: string) => string): Record<string, T> {
    return Object.entries(rec)
        .reduce<Record<string, T>>((acc, [k, v]) => ({...acc, [keyConverter(k)]: v}), {});
}

export function parseCSVFile<T> (text: string, factory: (index: number) => T, system: MeasurementUnit, csvRecords: Record<string, CSVPopulate<T>> | ColumnMatcher<T>[]): T[] {
    const results = Papa.parse<string[]>(text, {header: false});
    // // Example (header: false)
    // [
    //     ["Column 1", "Column 2"],
    //     ["foo", "bar"],
    //     ["abc", "def"]
    // ]
    
    if (results.data.length <= 1) { return [] }
    const headers = results.data[0]!;

    // Filter out empty rows
    const dataRows = results.data.slice(1).filter(row =>
        row.some(cell => cell.trim() !== '')
    )

    // Handle array-based matchers
    if (Array.isArray(csvRecords)) {
        return dataRows.map((row, index) => {
            const entry = factory(index)

            for (let column = 0; column < row.length; column++) {
                const header = headers[column]?.trim()
                if (!header) { continue }

                for (const matcher of csvRecords) {
                    if (matcher.match(header)) {
                        matcher.populate(row[column]!, entry, system)
                        break
                    }
                }
            }
            return entry
        })
    }

    // Handle record-based lookup (legacy)
    const headerLookup = convertKeys(csvRecords, k => k.toLowerCase().trim());

    return dataRows.map((row, index) => {
        const entry = factory(index)

        for (let column = 0; column < row.length; column++) {
            const header = headers[column]?.trim().toLowerCase()
            if (!header) { continue }
            
            const headerFunc = headerLookup[header]
            if (!headerFunc) { continue }

            headerFunc(row[column]!, entry, system)
        }
        return entry
    })
}
